// Client-side reconciliation engine (design spec §3.6 / §9.3 / §9.4 / §9.5).
//
// Identity (`eid`) and existence (time-range) are SEPARATE concerns:
//   - Label by the event's STAMPED `record_location` (D16) → CLOUD or SDCARD.
//   - Verify a *playable* clip on THAT side by a TIME-RANGE query (cloud:
//     recording_metadata overlap; sdcard: range-clip-existence / range-events).
//   - Dedup online + sdcard by `eid` byte-equality; surface OFFLINE sdcard events
//     (eid not on the server) as SDCARD_ONLY.
//   - sdcard TRI-STATE: UNREACHABLE → UNKNOWN_SDCARD, NEVER default to CLOUD.
//   - Time-aware PENDING grace for fresh cloud events (clip still uploading).
//
// There is NO `BOTH` label (D5): a single event is cloud XOR sdcard.
//
// Pure functions, no I/O — the cloud existence check and the per-fetch firstSeen
// map are INJECTED by the hook so this file is heavily unit-testable.

import type { PbEvent, SdcardState } from './sdcardQuery';

/** Final reconciled label (design spec §6.1 — five labels + the unreachable case). */
export type RecLabel =
  | 'CLOUD'
  | 'SDCARD'
  | 'SDCARD_ONLY'
  | 'PENDING'
  | 'EVENT_NO_RECORDING'
  | 'UNKNOWN_SDCARD';

/** GRACE = WINDOW_US (15s) + UPLOAD_LATENCY_US (default 30s) = 45s (§3.6 / §9.4). */
export const WINDOW_US = 15_000_000;
export const DEFAULT_UPLOAD_LATENCY_US = 30_000_000;
export const GRACE_US = WINDOW_US + DEFAULT_UPLOAD_LATENCY_US;

/** A server-aggregated event (REST GET /v3/devices/{id}/events), keyed by eid. */
export interface ServerEvent {
  eid: string;
  event_ts_us: number;
  event_type?: string;
  event_id_hex?: string;
  record_location: string;
  record_mode?: string;
  record_quality?: string;
  /** cloud snapshot presign (GET /v3/events/{eid}/snapshot), when available. */
  snapshot_url?: string;
}

/** A reconciled timeline row (design spec §6.1 row contract). */
export interface ReconciledRow {
  eid: string;
  event_ts_us: number;
  event_type?: string;
  event_id_hex?: string;
  /** which feeds carried this eid. */
  source: { server: boolean; sdcard: boolean };
  label: RecLabel;
  record_location: string;
  record_mode?: string;
  record_quality?: string;
  /** sdcard marker window (present when the sdcard feed carried it). */
  clip_start_us?: number;
  clip_end_us?: number;
  has_snapshot?: boolean;
  /** cloud snapshot presign URL (server side). */
  snapshot_url?: string;
}

export interface ReconcileInput {
  serverEvents: ServerEvent[];
  /** tri-state outcome of op:get range-events for this device. */
  sdcardState: SdcardState;
  /** sdcard markers (only consulted when sdcardState === 'ANSWERED'). */
  sdcardEvents: PbEvent[];
  /**
   * Does a CLOUD clip overlap this event's time? Injected by the hook from the
   * cloud recording_metadata time-range (eventually consistent → grace handles it).
   */
  cloudExists: (eventTsUs: number) => boolean;
  /** Wall clock (µs epoch) for the grace comparison. */
  nowUs: number;
  /**
   * Per-fetch "first seen" map (eid → µs) used as the grace anchor when an event's
   * `event_ts_us` is missing/0 (R14 guard — until the full-µs wire fix lands a fresh
   * cloud event with event_ts≈0 would otherwise be mislabeled EVENT_NO_RECORDING).
   */
  firstSeen?: Map<string, number>;
  /** Override grace window (µs) for tests / tuning. Defaults to GRACE_US. */
  graceUs?: number;
}

/**
 * Resolve the grace anchor: the event's own full-µs timestamp when usable, else the
 * per-fetch firstSeen timestamp (R14). Returns null when neither is usable — callers
 * then treat grace as inactive (cannot prove the clip is "fresh").
 */
function graceAnchorUs(ev: { eid: string; event_ts_us: number }, firstSeen?: Map<string, number>): number | null {
  if (typeof ev.event_ts_us === 'number' && ev.event_ts_us > 0) return ev.event_ts_us;
  const seen = firstSeen?.get(ev.eid);
  return typeof seen === 'number' && seen > 0 ? seen : null;
}

interface WorkRow {
  eid: string;
  event_ts_us: number;
  event_type?: string;
  event_id_hex?: string;
  labelIntent: string; // record_location
  sourceServer: boolean;
  sdcardOnly: boolean;
  sdcardPlayable: boolean;
  record_location: string;
  record_mode?: string;
  record_quality?: string;
  clip_start_us?: number;
  clip_end_us?: number;
  has_snapshot?: boolean;
  snapshot_url?: string;
}

/**
 * Reconcile server + sdcard event feeds into labeled rows (design spec §3.6).
 * Pure: all I/O (cloud overlap, firstSeen) is injected. Output is NOT sorted —
 * callers sort newest-first (eventsLib.compareRowsNewestFirst).
 */
export function reconcile(input: ReconcileInput): ReconciledRow[] {
  const { serverEvents, sdcardState, sdcardEvents, cloudExists, nowUs, firstSeen } = input;
  const graceUs = input.graceUs ?? GRACE_US;

  // Step 1 — seed from the server aggregate, keyed by eid.
  const byEid = new Map<string, WorkRow>();
  for (const e of serverEvents) {
    byEid.set(e.eid, {
      eid: e.eid,
      event_ts_us: e.event_ts_us,
      event_type: e.event_type,
      event_id_hex: e.event_id_hex,
      labelIntent: e.record_location,
      sourceServer: true,
      sdcardOnly: false,
      sdcardPlayable: false,
      record_location: e.record_location,
      record_mode: e.record_mode,
      record_quality: e.record_quality,
      snapshot_url: e.snapshot_url,
    });
  }

  // Step 2 — fold in the sdcard list, dedup by eid byte-equality. Only ANSWERED /
  // EMPTY run the diff; UNREACHABLE leaves availability UNKNOWN (handled in step 3).
  if (sdcardState === 'ANSWERED') {
    for (const s of sdcardEvents) {
      const existing = byEid.get(s.eid);
      if (existing) {
        // present on BOTH → dedup; mark sdcard-playable; enrich the marker window.
        existing.sdcardPlayable = true;
        existing.clip_start_us = s.clip_start_us;
        existing.clip_end_us = s.clip_end_us;
        existing.has_snapshot = existing.has_snapshot || s.has_snapshot;
        // The sdcard marker carries the authoritative D16 record_* stamp; the server
        // aggregate often omits record_mode/location. Enrich from the sdcard event so
        // downstream mode/location filters (e.g. the continuous timeline's
        // continuous+sdcard marker filter) see the real values instead of undefined.
        if (s.record_mode) existing.record_mode = s.record_mode;
        if (s.record_location) existing.record_location = s.record_location;
      } else {
        // OFFLINE sdcard event: never reached the server → SDCARD_ONLY.
        byEid.set(s.eid, {
          eid: s.eid,
          event_ts_us: s.event_ts_us,
          event_id_hex: s.event_id_hex,
          labelIntent: 'sdcard',
          sourceServer: false,
          sdcardOnly: true,
          sdcardPlayable: true,
          record_location: s.record_location || 'sdcard',
          record_mode: s.record_mode,
          record_quality: s.record_quality,
          clip_start_us: s.clip_start_us,
          clip_end_us: s.clip_end_us,
          has_snapshot: s.has_snapshot,
        });
      }
    }
  }

  // Step 3 — resolve each event's final label.
  const rows: ReconciledRow[] = [];
  for (const w of byEid.values()) {
    let label: RecLabel;
    if (w.labelIntent === 'sdcard') {
      if (w.sdcardPlayable || w.sdcardOnly) {
        label = w.sourceServer ? 'SDCARD' : 'SDCARD_ONLY';
      } else if (sdcardState === 'UNREACHABLE') {
        // device unreachable → unknown; NEVER assume cloud.
        label = 'UNKNOWN_SDCARD';
      } else {
        // stamped sdcard but no marker on a reachable device → no recording.
        label = 'EVENT_NO_RECORDING';
      }
    } else {
      // label_intent === 'cloud'
      if (cloudExists(w.event_ts_us)) {
        label = 'CLOUD';
      } else {
        const anchor = graceAnchorUs(w, firstSeen);
        if (anchor !== null && nowUs - anchor < graceUs) {
          label = 'PENDING'; // clip still uploading (eventually consistent, §9.4)
        } else {
          label = 'EVENT_NO_RECORDING';
        }
      }
    }

    rows.push({
      eid: w.eid,
      event_ts_us: w.event_ts_us,
      event_type: w.event_type,
      event_id_hex: w.event_id_hex,
      source: { server: w.sourceServer, sdcard: w.sdcardPlayable || w.sdcardOnly },
      label,
      record_location: w.record_location,
      record_mode: w.record_mode,
      record_quality: w.record_quality,
      clip_start_us: w.clip_start_us,
      clip_end_us: w.clip_end_us,
      has_snapshot: w.has_snapshot,
      snapshot_url: w.snapshot_url,
    });
  }

  return rows;
}

/**
 * Build a cloudExists predicate from a list of cloud clip time-ranges (the
 * recording_metadata overlap projection from GET /recordings?from&to). A clip
 * "exists" for an event when the event's ts falls within (or touches) a clip span.
 */
export function makeCloudExists(clips: Array<{ clip_start_ts: number; clip_end_ts: number }>): (eventTsUs: number) => boolean {
  // Sort once; linear scan is fine for a day's worth of clips, and callers memoize.
  const spans = clips
    .map((c) => ({ s: c.clip_start_ts, e: c.clip_end_ts }))
    .filter((c) => c.e >= c.s)
    .sort((a, b) => a.s - b.s);
  return (eventTsUs: number) => {
    for (const sp of spans) {
      if (eventTsUs >= sp.s && eventTsUs <= sp.e) return true;
      if (sp.s > eventTsUs) break;
    }
    return false;
  };
}

/** True for labels whose row has a playable clip the user can click to view. */
export function isPlayable(label: RecLabel): boolean {
  return label === 'CLOUD' || label === 'SDCARD' || label === 'SDCARD_ONLY';
}
